/**
 * Deal Comparables Service
 * Phase 5 Part 3: Historical deal comparisons for pricing insights
 * Uses backend API for data access
 */

import api from '../lib/api';

export const comparablesService = {
  /**
   * Find comparable deals based on asset characteristics
   * @param {Object} assetDetails - Asset details for matching
   * @param {string} assetDetails.therapeutic_area - Therapeutic area
   * @param {string} [assetDetails.stage] - Development stage
   * @param {string} [assetDetails.modality] - Drug modality
   * @param {number} [limit=15] - Maximum results
   */
  async findComparables(assetDetails, limit = 15) {
    const { therapeutic_area, stage, modality } = assetDetails;

    const params = { limit };
    if (therapeutic_area) params.therapeutic_area = therapeutic_area;
    if (stage) params.stage = stage;
    if (modality) params.modality = modality;

    const response = await api.get('/deals/comparables', { params });
    return response.data?.data || [];
  },

  /**
   * Get deal statistics for a therapeutic area and stage
   * @param {string} therapeuticArea - Therapeutic area
   * @param {string} [stage] - Development stage (optional)
   */
  async getDealStatistics(therapeuticArea, stage = null) {
    const params = {};
    if (therapeuticArea) params.therapeutic_area = therapeuticArea;
    if (stage) params.stage = stage;

    const response = await api.get('/deals/statistics', { params });
    return response.data?.data || {
      deal_count: 0,
      avg_upfront_usd_millions: null,
      median_upfront_usd_millions: null,
      min_upfront_usd_millions: null,
      max_upfront_usd_millions: null,
      avg_total_usd_millions: null,
      median_total_usd_millions: null,
      min_total_usd_millions: null,
      max_total_usd_millions: null,
      avg_milestone_usd_millions: null,
      recent_deal_count: 0
    };
  },

  /**
   * Get deal trends by year
   * @param {string} [therapeuticArea] - Filter by therapeutic area
   * @param {number} [years=5] - Number of years to look back
   */
  async getDealTrends(therapeuticArea = null, years = 5) {
    const params = { years };
    if (therapeuticArea) params.therapeutic_area = therapeuticArea;

    const response = await api.get('/deals/trends', { params });
    return response.data?.data || [];
  },

  /**
   * Get top acquirers/buyers in a therapeutic area
   * @param {string} [therapeuticArea] - Filter by therapeutic area
   * @param {number} [limit=10] - Maximum results
   */
  async getTopAcquirers(therapeuticArea = null, limit = 10) {
    const params = { limit };
    if (therapeuticArea) params.therapeutic_area = therapeuticArea;

    const response = await api.get('/deals/top-acquirers', { params });
    return response.data?.data || [];
  },

  /**
   * Direct query to deals with filters (fallback)
   * @param {Object} filters - Filter options
   */
  async queryDeals(filters = {}) {
    return this.findComparables(filters, filters.limit || 15);
  },

  /**
   * Calculate local statistics from deal data
   * (Use when API data is not available)
   * @param {Array} deals - Array of deal objects
   */
  calculateLocalStatistics(deals) {
    if (!deals || deals.length === 0) {
      return {
        deal_count: 0,
        avg_upfront: null,
        median_upfront: null,
        avg_total: null,
        median_total: null
      };
    }

    const upfronts = deals
      .map(d => d.upfront_payment_usd_millions)
      .filter(v => v != null && v > 0);

    const totals = deals
      .map(d => d.total_value_usd_millions)
      .filter(v => v != null && v > 0);

    return {
      deal_count: deals.length,
      avg_upfront: average(upfronts),
      median_upfront: median(upfronts),
      min_upfront: upfronts.length > 0 ? Math.min(...upfronts) : null,
      max_upfront: upfronts.length > 0 ? Math.max(...upfronts) : null,
      avg_total: average(totals),
      median_total: median(totals),
      min_total: totals.length > 0 ? Math.min(...totals) : null,
      max_total: totals.length > 0 ? Math.max(...totals) : null
    };
  }
};

// Helper functions
function average(arr) {
  if (!arr || arr.length === 0) return null;
  return Math.round(arr.reduce((a, b) => a + b, 0) / arr.length * 100) / 100;
}

function median(arr) {
  if (!arr || arr.length === 0) return null;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? Math.round((sorted[mid - 1] + sorted[mid]) / 2 * 100) / 100
    : sorted[mid];
}

export default comparablesService;
